import { useEffect, useRef } from 'react';
import Eyebrow from './Eyebrow';

/**
 * The phone's menu: a panel that rises from above the tab bar, with a grab
 * handle, a title and rows. The same shape the More sheet (SettingsFooter)
 * has, for the menus a screen opens itself — a file list, the versions of
 * the code, the app's actions.
 *
 * Only on the phone: at `lg` the same choices are a dropdown beside what
 * they act on, and this renders nothing.
 */

export interface SheetRow {
  label: string;
  /** Dimmed, on the right: a state, a date, a short address. */
  meta?: string;
  onClick: () => void;
  isActive?: boolean;
  isDanger?: boolean;
  isDisabled?: boolean;
}

const BottomSheet = ({ open, onClose, title, children }: {
  open: boolean,
  onClose: () => void,
  title: React.ReactNode,
  children: React.ReactNode,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClose();
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return (<>
    <div aria-hidden="true" className="lg:hidden fixed inset-0 z-30 bg-navy-950/60 backdrop-blur-md" />
    <div
      ref={ref}
      role="dialog"
      aria-modal="true"
      className="lg:hidden fixed z-30 left-0 right-0 bottom-[calc(61px_+_env(safe-area-inset-bottom))] short:bottom-[calc(41px_+_env(safe-area-inset-bottom))] mx-3 mb-2 rounded-container border border-navy-500 bg-navy-800 shadow-[0_8px_32px_rgba(0,0,0,.5)] overflow-y-auto b4-thin max-h-[min(80vh,36rem,calc(100vh_-_120px))]"
    >
      <div className="sticky top-0 z-10 bg-navy-800 px-4 pt-2 pb-2.5 border-b border-navy-500">
        <div aria-hidden="true" className="mx-auto mb-2.5 w-8 h-1 rounded-full bg-navy-500" />
        <h2 className="m-0 font-inter text-ds-135 font-semibold text-white truncate">{title}</h2>
      </div>
      {children}
    </div>
  </>);
};

/** A block of rows under an eyebrow: what the rows are about. */
export const SheetGroup = ({ label, children }: { label: string, children: React.ReactNode }) => (
  <div className="border-b border-navy-500 last:border-b-0">
    <div className="px-4 pt-3.5 pb-1.5 short:pt-2.5 short:pb-1"><Eyebrow>{label}</Eyebrow></div>
    {children}
  </div>
);

/** A list of rows in a sheet, ruled by the 1px gaps over a navy ground. */
export const SheetRows = ({ rows, onPick }: { rows: SheetRow[], onPick?: () => void }) => (
  <div className="flex flex-col gap-px bg-navy-700">
    {rows.map(row => (
      <button
        key={row.label}
        type="button"
        disabled={row.isDisabled}
        onClick={() => { onPick?.(); row.onClick(); }}
        className={[
          'b4-focus w-full min-h-[48px] short:min-h-[38px] px-4 flex items-center gap-3 text-left text-ds-135',
          'transition-colors duration-[120ms] disabled:opacity-50',
          row.isActive
            ? 'bg-navy-600 text-white font-semibold'
            : `bg-navy-800 ${row.isDanger ? 'text-state-err' : 'text-navy-100'}`,
        ].join(' ')}
      >
        <span className="flex-1 min-w-0 truncate">{row.label}</span>
        {row.meta ? (
          <span className="flex-none font-courrier text-ds-115 text-navy-300 truncate max-w-[45%]">{row.meta}</span>
        ) : null}
      </button>
    ))}
  </div>
);

export default BottomSheet;
